<template>
  <div
    ref="searchTripRef"
    class="SearchTrip"
    @focusin="expandActionRow"
    @focusout="collapseActionRowOnFocusOut"
    @click="expandActionRow"
  >
    <div class="SearchTrip-content">
      <header class="SearchTrip-header">
        <h2 class="SearchTrip-title">
          {{ t('ui.booking.search.title') }}
        </h2>

        <div v-if="allowBothTripTypes" class="SearchTrip-toggleGroup">
          <ToggleButton
            v-model="currentTripType"
            name="tripType"
            :options="toggleOptions"
            size="sm"
            class="SearchTrip-toggle"
            aria-label="Trip type selection"
          />
          <div v-if="disclaimerText" class="SearchTrip-disclaimer">
            <Icon icon="information" size="xs" class="SearchTrip-disclaimerIcon" />
            <span class="SearchTrip-disclaimerText">{{ disclaimerText }}</span>
          </div>
        </div>
      </header>

      <form class="SearchTrip-form" @submit.prevent="onSubmit">
        <div class="SearchTrip-locationRow">
          <Autocomplete
            v-model="originValue"
            name="origin"
            :label="t('ui.booking.search.origin.label')"
            hideAsterisk
            :sideOffset="12"
            :placeholder="t('ui.booking.search.origin.placeholder')"
            :disabled
            :options="originOptions"
            clearable
            class="SearchTrip-autocomplete"
            v-bind="originProps"
          />

          <div class="SearchTrip-divider" />

          <Autocomplete
            v-model="destinationValue"
            name="destination"
            :label="t('ui.booking.search.destination.label')"
            hideAsterisk
            :sideOffset="12"
            :placeholder="t('ui.booking.search.destination.placeholder')"
            :disabled
            :options="destinationOptions"
            :initialValue="destinationInitialValue"
            clearable
            :leftIcon="destinationValue === ANYWHERE_GROUP_CODE ? 'creation' : undefined"
            class="SearchTrip-autocomplete"
            v-bind="destinationProps"
          >
            <template v-if="enableAnywhereDestination || customGroupOptions.length > 0" #prepend>
              <template v-if="enableAnywhereDestination">
                <ComboboxItem class="SearchTrip-anywhere" :value="anywhereOption">
                  <Icon icon="creation" color="primary" />
                  <div class="SearchTrip-anywhereCopy">
                    <span class="SearchTrip-anywhereTitle">{{ t('ui.booking.search.destination.anywhere.title') }}</span>
                    <span class="SearchTrip-anywhereDescription">{{ t('ui.booking.search.destination.anywhere.description') }}</span>
                  </div>

                  <ActionButton
                    rightIcon="arrow-right"
                    class="SearchTrip-anywhereButton"
                    size="sm"
                    variant="secondary"
                    :ariaLabel="t('ui.booking.search.destination.anywhere.buttonAriaLabel')"
                  />
                </ComboboxItem>

                <div v-if="customGroupOptions.length > 0" class="SearchTrip-divider SearchTrip-divider--horizontal" />
              </template>

              <div v-if="customGroupOptions.length > 0" class="SearchTrip-groupTagsSection">
                <span class="SearchTrip-groupTagsLabel">
                  {{ t('ui.booking.search.destination.groups.label') }}
                </span>
                <div class="SearchTrip-groupTags">
                  <ComboboxItem
                    v-for="group of customGroupOptions"
                    :key="group.value"
                    :value="group"
                    class="SearchTrip-groupTag"
                  >
                    <Tag
                      size="sm"
                      variant="outlined"
                      type="neutral"
                      :leftIcon="group.leftIcon"
                    >
                      {{ group.label }}
                    </Tag>
                  </ComboboxItem>
                </div>
              </div>

              <div class="SearchTrip-divider SearchTrip-divider--horizontal" />
            </template>
          </Autocomplete>
        </div>

        <div ref="actionRowWrapperRef" class="SearchTrip-actionRowWrapper" :class="{ 'is-expanded': isActionRowExpanded }">
          <div class="SearchTrip-actionRow" :inert="!isActionRowExpanded || undefined">
            <div class="SearchTrip-dateGroup">
              <DatePicker
                ref="datePickerRef"
                v-model="selectedDates"
                name="departureDate"
                :range="isCurrentTripTypeRT"
                :label="t('ui.booking.search.departure.date.label')"
                hideAsterisk
                required
                allowSameDayRange
                :disabled
                :minDate="minSearchDate"
                :maxDate="maxSearchDate"
                :startField="departureInputProps"
                :endField="returnInputProps"
                teleport="#teleports"
                :anytime="isAnytime"
                @open="isDatePickerOpen = true"
                @closed="isDatePickerOpen = false"
              />
            </div>

            <div v-show="showPassengersInput" class="SearchTrip-passengersGroup">
              <PassengersInput
                v-model="selectedPassengerIds"
                v-model:seatTypes="selectedSeatTypes"
                name="passengers"
                :passengers
                :maxPassengers
                :messages="passengerMessages"
                :hideAddNewPassenger
                :showSeatSelection
                :tripType="currentTripType"
                :label="t('ui.global.passenger')"
                :placeholder="t('ui.booking.search.passengers.addPassengers')"
                :disabled
                :sideOffset="4"
                :error="errors.passengers"
                :evaluating="passengersEvaluating"
                @add-new="$emit('addNewPassenger')"
                @evaluate="evaluatedPassengers = $event"
              />
            </div>

            <ActionButton
              type="submit"
              size="md"
              variant="primary"
              rightIcon="chevron-right"
              :disabled
              class="SearchTrip-searchGroup"
            >
              {{ t('ui.booking.search.button.text') }}
            </ActionButton>
          </div>
        </div>
      </form>
    </div>
  </div>
</template>

<script setup lang="ts">
import { onClickOutside, useElementSize } from '@vueuse/core';
import { ref, computed, watch, watchEffect } from 'vue';
import { useI18n } from '@cvo/i18n/composables/useI18n';
import { useForm } from 'vee-validate';
import { toTypedSchema } from '@vee-validate/yup';
import * as yup from 'yup';
import { ComboboxItem } from 'reka-ui';
import { useRouteOptions, type DestinationGroupOption } from '@cvo/ui/composables';
import { type Destination, type GroupItem, type SeatType, type StationGroupType, type StationItem, type TripType, TripTypes, ANYWHERE_GROUP_CODE, resolvePassengerSeating } from '@cvo/domain';
import ActionButton from '../../atoms/ActionButton/ActionButton.vue';
import Icon from '../../atoms/Icon/Icon.vue';
import Tag from '../../atoms/Tag/Tag.vue';
import DatePicker, { type DatesSelection } from '../../molecules/DatePicker/DatePicker.vue';
import Autocomplete from '../../molecules/Autocomplete/Autocomplete.vue';
import PassengersInput from '../../molecules/PassengersInput/PassengersInput.vue';
import type { PassengersInputSelection } from '../../molecules/PassengersInput/PassengersInput.model';
import ToggleButton from '../../atoms/ToggleButton/ToggleButton.vue';
import type { ToggleButtonOption } from '../../atoms/ToggleButton/ToggleButton.model';
import type { InputProps } from '../../atoms';
import type { SearchTripProps, SearchTripEmits, SearchTripParams, BookingWindowDates  } from './SearchTrip.model';

const props = withDefaults(defineProps<SearchTripProps>(), {
  tripRoutes: () => [],
  tripType: TripTypes.ALL,
  passengers: () => [],
  passengersValid: true,
});

const emit = defineEmits<SearchTripEmits>();
const { t } = useI18n();

/* ========================================
 * Trip Type Management
 * ======================================== */

const allowBothTripTypes = computed(() => props.tripType === TripTypes.ALL);
// eslint-disable-next-line vue/no-ref-object-reactivity-loss
const initialTripType = allowBothTripTypes.value
  // eslint-disable-next-line vue/no-setup-props-reactivity-loss
  ? props.selectedValues?.tripType ?? TripTypes.RT // If ALL allowed, then we can safely use the value passed as selected.
  : props.tripType; // If ALL is not allowed, then we need to force using the only allowed value.
const currentTripType = ref<TripType>(initialTripType);

const isCurrentTripTypeRT = computed(() => currentTripType.value === TripTypes.RT);

const toggleOptions: ToggleButtonOption[] = [
  { label: t('ui.global.tripType.OW'), value: TripTypes.OW },
  { label: t('ui.global.tripType.RT'), value: TripTypes.RT },
];

/* ========================================
 * Collapsible action row
 * ======================================== */

/** Starts collapsed only when `collapsible` is set. */
// eslint-disable-next-line vue/no-setup-props-reactivity-loss
const isActionRowExpanded = ref(!props.collapsible);

function expandActionRow() {
  isActionRowExpanded.value = true;
}

const searchTripRef = ref<HTMLElement>();

// Avoids a race where vue-datepicker's own click-outside/refocus logic makes a date pick look like an outside click.
const isDatePickerOpen = ref(false);

// Matches our own popups teleported outside this component's DOM subtree, so they aren't treated as "outside".
const OWNED_TELEPORT_SELECTOR = '#teleports, [data-reka-popper-content-wrapper], .PanelDrawer, .PanelDrawer-overlay';

function isOwnedElement(element: Element | null): boolean {
  if (!element) return false;
  return !!searchTripRef.value?.contains(element) || !!element.closest(OWNED_TELEPORT_SELECTOR);
}

onClickOutside(searchTripRef, (_event) => {
  // Picking a date can race the ignore-list check below, so also skip while the picker is open.
  if (!props.collapsible || isDatePickerOpen.value) return;
  isActionRowExpanded.value = false;
}, { ignore: [OWNED_TELEPORT_SELECTOR] });

function collapseActionRowOnFocusOut(event: FocusEvent) {
  // Same race as above: don't rely solely on relatedTarget while the picker is open.
  if (!props.collapsible || isDatePickerOpen.value) return;
  if (isOwnedElement(event.relatedTarget as Element | null)) return;
  isActionRowExpanded.value = false;
}

const actionRowWrapperRef = ref<HTMLElement>();
const { height: rootHeight } = useElementSize(searchTripRef, undefined, { box: 'border-box' });
const { height: expansionHeight } = useElementSize(actionRowWrapperRef, undefined, { box: 'border-box' });

const collapsedHeight = computed(() => rootHeight.value - expansionHeight.value);
watch(collapsedHeight, (height) => {
  if (rootHeight.value) emit('collapsedHeight', height);
});

/* ========================================
 * Form State & Validation
 * ======================================== */

const selectedPassengerIds = ref<string[]>([]);
const selectedSeatTypes = ref<Record<string, SeatType>>({});
/** Selection shown by the passengers panel, which may differ from the committed one while drafting. */
const evaluatedPassengers = ref<PassengersInputSelection>();

const blockingMessage = computed(() => props.passengerMessages?.find(message => message.status === 'danger')?.message);

/**
 * Passenger composition is validated server-side. Submitting is blocked until an evaluation came
 * back valid, so an in-flight or failed evaluation is an error too, not a pass.
 */
const passengersError = computed<string>(() => {
  // An empty selection is not evaluated: the "at least one passenger" rule reports it instead.
  if (!selectedPassengerIds.value.length) return '';
  if (props.passengersEvaluating) return t('ui.booking.search.passengers.evaluation.pending');
  if (props.passengersValid) return '';

  return blockingMessage.value ?? t('ui.booking.search.passengers.evaluation.unavailable');
});

const optionalDate = yup.date().nullable();

const schema = computed(() => toTypedSchema(yup.object({
  origin: yup.string().required(t('ui.form.validation.required', { field: t('ui.booking.search.origin.label') })),
  destination: yup.string().required(t('ui.form.validation.required', { field: t('ui.booking.search.destination.label') })),
  departureDate: props.optionalDates
    ? optionalDate
    : yup.date()
      .nullable()
      .required(t('ui.form.validation.required', { field: t('ui.booking.search.departure.date.label') })),
  returnDate: (isCurrentTripTypeRT.value && !props.optionalDates)
    ? yup.date()
      .nullable()
      .required(t('ui.form.validation.required', { field: t('ui.booking.search.return.date.label') }))
    : optionalDate,
  passengers: props.showPassengersInput
    ? yup.array().of(yup.string())
      .min(1, t('ui.form.validation.requiredPlural', { field: t('ui.global.passenger') }))
      .test('evaluation', passengersError.value, () => !passengersError.value)
    : yup.array()
      .of(yup.string())
      .notRequired(),
})));

const { defineField, setFieldValue, validateField, handleSubmit, submitCount, errors } = useForm({ name: 'searchTrip', validationSchema: schema });

const shouldValidate = computed(() => submitCount.value > 0);

function validationConfig() {
  return { validateOnBlur: false, validateOnChange: false, validateOnModelUpdate: shouldValidate.value };
}

const [originValue, originProps] = defineField('origin', validationConfig);
const [destinationValue, destinationProps] = defineField('destination', validationConfig);
const [, departureProps] = defineField<'departureDate', Date | null>('departureDate', validationConfig);
const [, returnProps] = defineField<'returnDate', Date | null>('returnDate', validationConfig);

const departureInputProps = computed<InputProps>(() => ({
  name: 'departureDate',
  label: t('ui.booking.search.departure.date.label'),
  required: true,
  ...departureProps.value,
}));

const returnInputProps = computed<InputProps>(() => ({
  name: 'returnDate',
  label: t('ui.booking.search.return.date.label'),
  required: true,
  ...returnProps.value,
}));

/** True when dates are optional and none has been chosen — both date inputs display "Anytime". */
const isAnytime = computed<boolean>(() => props.optionalDates && !selectedDates.value);

const simpleRoutes = computed(() => {
  return props.tripRoutes.map((tripRoute) => ({
    origin: tripRoute.origin,
    destinations: tripRoute.destinations.map((dest) => dest.destination),
  }));
});

function isStationGroup(station: StationItem): station is GroupItem {
  return station.type === 'STATION_GROUP';
}

interface GroupAccumulator {
  groupType?: StationGroupType;
  stations: Set<string>;
}

/**
 * Merges a group's stations into `groupsByCode`, which accumulates the union of member codes seen
 * across every route passed in — a later, partial listing must not drop members already found for
 * that group code.
 */
function mergeGroupStations(dest: Destination, groupsByCode: Map<string, GroupAccumulator>) {
  if (!isStationGroup(dest)) return;

  const group = groupsByCode.get(dest.destination) ?? { groupType: dest.groupType, stations: new Set<string>() };
  groupsByCode.set(dest.destination, group);

  (dest.stations ?? []).forEach(station => group.stations.add(station.code));
}

function buildStationGroups(destinations: Destination[]): DestinationGroupOption[] {
  const groupsByCode = new Map<string, GroupAccumulator>();
  destinations.forEach(dest => mergeGroupStations(dest, groupsByCode));

  return Array.from(groupsByCode, ([code, { groupType, stations }]) => ({ code, groupType, stations: Array.from(stations) }));
}

/** The selected origin's own route entry, with its raw per-destination station-group/booking-window data. */
const selectedOriginRoute = computed(() => props.tripRoutes.find(route => route.origin === originValue.value));

/** Selectable station-group destinations, with their member stations, merged across every origin's route. */
const allStationGroups = computed<DestinationGroupOption[]>(() => buildStationGroups(props.tripRoutes.flatMap(route => route.destinations)));

/** Destination-side station groups, scoped to the selected origin's own route once one is chosen. */
const destinationGroups = computed<DestinationGroupOption[]>(() => (selectedOriginRoute.value ? buildStationGroups(selectedOriginRoute.value.destinations) : allStationGroups.value));

const {
  origin,
  destination,
  originOptions,
  destinationOptions,
  anywhereOption,
  customGroupOptions,
} = useRouteOptions(
  simpleRoutes,
  { origin: originValue, destination: destinationValue },
  destinationGroups,
  { enableBrowseGroups: () => props.enableCityGroup, originGroups: allStationGroups },
);

const selectedDestination = computed<Destination | undefined>(() => {
  return selectedOriginRoute.value?.destinations.find((dest) => dest.destination === destination.value);
});

/** Initial value for the destination autocomplete — covers options not in the regular list (ANYWHERE, CUSTOM_GROUP). */
const destinationInitialValue = computed(() => {
  if (!destinationValue.value) return undefined;
  if (destinationValue.value === ANYWHERE_GROUP_CODE) return anywhereOption;
  return customGroupOptions.value.find(opt => opt.value === destinationValue.value);
});
/** Booking window associated to the selected route */
const appliedBookingWindow = computed<BookingWindowDates | undefined>(() => {
  const bookingWindow = selectedDestination.value?.bookingWindow;
  if (!bookingWindow) return undefined;

  return {
    startDate: new Date(bookingWindow.startDate),
    endDate: new Date(bookingWindow.endDate),
  };
});

/** Minimum date available for search. It depends on the booking window of the selected route. */
const minSearchDate = computed<Date | undefined>(() => {
  return appliedBookingWindow.value?.startDate || props.minDate;
});

/** Maximum date available for search. It depends on the booking window of the selected route. */
const maxSearchDate = computed<Date | undefined>(() => {
  return appliedBookingWindow.value?.endDate || props.maxDate;
});

const selectedDates = ref<DatesSelection>(null);

const departDate = computed<Date | null>(() => {
  return Array.isArray(selectedDates.value) ? selectedDates.value[0] : selectedDates.value;
});

const returnDate = computed<Date | null>(() => {
  return Array.isArray(selectedDates.value) ? selectedDates.value[1] : null;
});

watch([departDate, returnDate], () => {
  setFieldValue('departureDate', departDate.value || undefined, shouldValidate.value);
  setFieldValue('returnDate', returnDate.value || undefined, shouldValidate.value);
});

watch(selectedPassengerIds, () => {
  if (!props.showPassengersInput) return;
  setFieldValue('passengers', selectedPassengerIds.value, shouldValidate.value);
}, { deep: true, immediate: true });

// The evaluation resolves after the selection changed, so the schema message is stale until refreshed.
watch(passengersError, () => {
  if (shouldValidate.value) validateField('passengers');
});

watch(() => props.selectedValues, (newSelectedValues) => {
  if (!newSelectedValues) return;

  origin.value = newSelectedValues.origin;
  destination.value = newSelectedValues.destination;

  // @ts-expect-error Null in return date is not expected to be emitted, but required for preselection.
  selectedDates.value = isCurrentTripTypeRT.value
    ? (newSelectedValues.departDate ? [newSelectedValues.departDate, newSelectedValues.returnDate ?? null] : null)
    : newSelectedValues.departDate;

  syncSelectedPassengers(newSelectedValues);
}, { immediate: true, deep: true });

function syncSelectedPassengers({ passengerIds, seatTypes }: Partial<SearchTripParams>) {
  selectedPassengerIds.value = passengerIds ?? [];
  selectedSeatTypes.value = { ...seatTypes };
}

// Immediate so the evaluation also runs on load, which is where the passenger limit comes from.
watchEffect(() => {
  if (!props.showPassengersInput || !evaluatedPassengers.value) return;
  emit('passengersEvaluate', {
    passengers: resolvePassengerSeating(props.passengers, evaluatedPassengers.value.passengerIds, evaluatedPassengers.value.seatTypes),
    travelDate: departDate.value ?? appliedBookingWindow.value?.startDate,
    tripType: currentTripType.value,
  });
});

const onSubmit = handleSubmit((values) => {
  emit('search', {
    origin: values.origin,
    destination: values.destination,
    departDate: values.departureDate ?? undefined,
    returnDate: values.returnDate ?? undefined,
    passengerIds: props.showPassengersInput ? selectedPassengerIds.value : undefined,
    seatTypes: props.showPassengersInput && props.showSeatSelection ? selectedSeatTypes.value : undefined,
    tripType: currentTripType.value,
  } satisfies SearchTripEmits['search'][0]);
}, ({ errors: submitErrors }) => {
  console.error('Form validation errors:', submitErrors);
  emit('error', submitErrors);
});
</script>

<style lang="scss">
@use '@cvo/ui/styles/design-system' as vars;
@use '@cvo/ui/styles/utils/text';

@layer cvo-ui {
  @mixin section-container {
    background-color: vars.$molecules-searchtrip-box-inputs-bg-color;
    border: vars.$molecules-searchtrip-box-inputs-border-stroke solid vars.$molecules-searchtrip-box-inputs-border-color;
    border-radius: vars.$molecules-searchtrip-box-inputs-border-radius;
    padding: vars.$molecules-searchtrip-box-inputs-padding-top-bottom vars.$molecules-searchtrip-box-inputs-padding-left-right;
  }

  // Mixin for stacked (vertical) layout
  @mixin stacked-layout {
    display: flex;
    flex-direction: column;
    align-items: stretch;
    gap: var(--section-gap);
  }

  // Mixin for responsive stacking behavior
  @mixin responsive-stacking {
    .SearchTrip-actionRow {
      display: flex;
      flex-direction: column;
      align-items: stretch;
      gap: var(--form-gap);
    }

    .SearchTrip-locationRow {
      @include stacked-layout;
    }

    .SearchTrip-dateGroup,
    .SearchTrip-passengersGroup,
    .SearchTrip-searchGroup {
      width: 100%;
    }
  }

  .SearchTrip {
    --content-gap: #{vars.$dimensions-d-6};
    --form-gap: #{vars.$dimensions-d-5};
    --section-gap: #{vars.$dimensions-d-3};

    background-color: vars.$molecules-searchtrip-box-bg-color;
    border: vars.$molecules-searchtrip-box-border-stroke solid vars.$molecules-searchtrip-box-border-color;
    border-radius: vars.$molecules-searchtrip-box-lg-md-border-radius;
    width: 100%;
    color: vars.$molecules-searchtrip-box-disclaimer-color;

    .Autocomplete-label,
    .Input-label {
      font-weight: vars.$font-weight-text-500;
      line-height: normal;
    }

    &-content {
      padding: vars.$molecules-searchtrip-box-lg-md-padding-top-bottom vars.$molecules-searchtrip-box-lg-md-padding-left-right;
      display: flex;
      flex-direction: column;
      gap: var(--content-gap);
    }

    &-header {
      width: 100%;
      display: flex;
      align-items: flex-start;
      justify-content: space-between;
      gap: vars.$dimensions-d-4;

      background-color: vars.$molecules-searchtrip-header-bg-color;
      border: vars.$molecules-searchtrip-header-border-stroke solid vars.$molecules-searchtrip-header-border-color;
      border-radius: vars.$molecules-searchtrip-header-lg-md-border-radius;
      padding: vars.$molecules-searchtrip-header-lg-md-padding-top-bottom vars.$molecules-searchtrip-header-lg-md-padding-left-right;
    }

    &-title {
      color: vars.$molecules-searchtrip-box-disclaimer-color;
      @include text.heading(h4);
      margin: 0;
    }

    &-form {
      display: flex;
      flex-direction: column;
      width: 100%;
    }

    &-locationRow {
      @include section-container;
      display: flex;
      gap: var(--section-gap);
    }

    &-actionRowWrapper {
      display: grid;
      // Explicit 0 minimum: with `auto` the row's own padding would floor the track and keep showing.
      grid-template-rows: minmax(0, 0fr);
      opacity: 0;
      overflow: hidden;
      pointer-events: none;
      transition:
        grid-template-rows vars.$transition-base ease-out,
        opacity vars.$transition-base ease-out;

      &.is-expanded {
        grid-template-rows: minmax(0, 1fr);
        opacity: 1;
        pointer-events: auto;
      }
    }

    &-actionRow {
      display: flex;
      align-items: stretch;
      gap: var(--form-gap);
      min-width: 0;
      min-height: 0;
      padding-block-start: var(--content-gap); // inside the collapsing track, so it vanishes with it
    }

    // Date Group Container
    &-dateGroup {
      @include section-container;
      display: flex;
      gap: var(--section-gap);
      flex: 1.5;
      min-width: 0;

      > .DatePicker {
        width: 100%;
      }

      .Input-field {
        height: 29px;
      }
    }

    &-searchGroup {
      align-self: flex-end;
    }

    .Autocomplete-field, .Input-field, .PassengersInput-field {
      border: none !important; // Prevent any Autocomplete/PassengersInput border.
    }
    .PassengersInput-field {
      padding-block: 2px; // Reduce default input padding to match SearchTrip style (height)
    }

    // Slight focus style since we don't have any border anymore.
    .Input, .Autocomplete {
      &:focus-within {
        .Input-label, .Autocomplete-label {
          font-weight: vars.$font-weight-text-600;
        }
      }
    }

    .DatePicker {
      &-divider {
        width: 0;
        border-inline-start: 1px solid vars.$molecules-searchtrip-box-divider-color;
        border-top: none;
        align-self: stretch;

        @container (max-width: #{vars.$screen-sm-max-width}) {
          width: 100%;
          height: 0;
          border-inline-start: none;
          border-top: 1px solid vars.$molecules-searchtrip-box-divider-color;
          margin: var(--section-gap) 0;
        }
      }
    }

    // Dividers
    &-divider {
      width: 0;
      border-inline-start: 1px solid vars.$molecules-searchtrip-box-divider-color;
      border-top: none;

      &--horizontal {
        width: 100%;
        height: 0;
        border-inline-start: none;
        border-top: 1px solid vars.$molecules-searchtrip-box-divider-color;
      }
    }

    &-passengersGroup {
      flex: 1;
      min-width: 0;

      .PanelDropdown-trigger {
        height: 100%;
        // Set on child trigger so the trigger gets the padding, and its width is used for the dropdown width.
        // Otherwise the --reka-popper-anchor-width variable would not take into account the padding.
        @include section-container;
      }
    }

    &-anywhere {
      display: flex;
      align-items: flex-start;
      justify-content: flex-start;
      gap: vars.$dimensions-d-3;
      padding: vars.$dimensions-d-3;
      cursor: pointer;

      &[data-highlighted] {
        background-color: vars.$atoms-dropdown-baseitem-hover-bg-color;
        color: vars.$atoms-dropdown-baseitem-hover-label-color;
      }
    }

    &-anywhereCopy {
      display: flex;
      flex-direction: column;
      gap: vars.$dimensions-d-1;
    }

    &-anywhereTitle {
      color: vars.$atoms-autocomplete-baseitem-default-label-color;
      // TODO CVO-19983: Replace with semantic token once available
      @include text.text(sm, vars.$font-weight-text-700);
    }

    &-anywhereDescription {
      color: vars.$atoms-autocomplete-baseitem-default-secondarytext-color;
      @include text.text(xs);
    }

    &-anywhereButton {
      min-width: 0;
      margin-left: auto;
    }

    &-groupTagsSection {
      display: flex;
      flex-direction: column;
      gap: vars.$dimensions-d-2;
      padding: vars.$dimensions-d-3;
    }

    &-groupTagsLabel {
      @include text.text(xs);
      color: vars.$atoms-autocomplete-baseitem-default-secondarytext-color;
    }

    &-groupTags {
      display: flex;
      flex-wrap: wrap;
      gap: vars.$dimensions-d-2;
    }

    &-groupTag {
      cursor: pointer;

      &[data-highlighted] .Tag {
        --tag-border-color: #{vars.$atoms-dropdown-baseitem-hover-bg-color};
        --tag-bg-color: #{vars.$atoms-dropdown-baseitem-hover-bg-color};
      }
    }

    &-toggleGroup {
      display: flex;
      flex-direction: column;
      align-items: flex-end;
      gap: vars.$dimensions-d-2;
    }

    &-disclaimer {
      display: flex;
      align-items: center;
      gap: vars.$dimensions-d-1;
      font-size: vars.$font-size-xs;
      color: vars.$molecules-searchtrip-box-disclaimer-color;
    }

    &-disclaimerIcon {
      color: vars.$molecules-searchtrip-box-disclaimer-icon-color;
    }

    // Medium container: when container width is up to medium breakpoint
    @container (max-width: #{vars.$screen-sm-max-width}) {
      @include responsive-stacking;

      &-header {
        flex-direction: column;
      }

      &-toggleGroup {
        align-items: flex-start;
      }

      &-passengersGroup > .PassengersInput {
        @include section-container;
      }
    }

    // Small container: when container width is up to small breakpoint
    @container (max-width: #{vars.$screen-sm-max-width}) {
      border-radius: vars.$molecules-searchtrip-box-sm-border-radius;

      .SearchTrip-content {
        padding: vars.$molecules-searchtrip-box-sm-padding-top-bottom vars.$molecules-searchtrip-box-sm-padding-left-right;
        --content-gap: #{vars.$dimensions-d-5};
        --form-gap: #{vars.$dimensions-d-5};
      }

      .SearchTrip-header {
         border-radius: vars.$molecules-searchtrip-header-sm-border-radius;
         padding: vars.$molecules-searchtrip-header-sm-padding-top-bottom vars.$molecules-searchtrip-header-sm-padding-left-right;
      }

      .SearchTrip-dateGroup {
        @include stacked-layout;
      }
    }
  }
}
</style>